/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ApiRootService } from '@common/api-root-shared';
import { BinaryFileListResource } from '@common/binary-file-shared';
import {
  CommandOrder,
  CommandResource,
  CommandService,
  CreateCommandActionProps,
  CreateCommandActionPropsWithoutResource,
  getEffectedResourceUrl,
  tapOnCommandSuccessfullyDone,
} from '@alfa-client/command-shared';
import { Environment, ENVIRONMENT_CONFIG } from '@common/environment-shared';
import { NavigationService } from '@alfa-client/navigation-shared';
import {
  createEmptyStateResource,
  doIfLoadingRequired,
  isLoaded,
  isNotNil,
  isNotNull,
  mapToResource,
  StateResource,
} from '@common/tech-shared';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { hasLink, ResourceUri } from '@ngxp/rest';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, first, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { VorgangActions } from './+state/vorgang.actions';
import { VorgangFacade } from './+state/vorgang.facade';
import { VorgangSelectors } from './+state/vorgang.selectors';
import { VorgangNavigationUtil } from './vorgang-navigation.util';
import { VorgangWithEingangLinkRel } from './vorgang.linkrel';
import { AdditionalActions, VorgangWithEingangResource } from './vorgang.model';
import { createMoveAndDeleteCommand, createProcessVorgangCommand } from './vorgang.util';

@Injectable({ providedIn: 'root' })
export class VorgangService {
  private readonly envConfig = inject<Environment>(ENVIRONMENT_CONFIG);
  private readonly navigationService = inject(NavigationService);
  private readonly facade = inject(VorgangFacade);
  private readonly apiRootService = inject(ApiRootService);
  private readonly commandService = inject(CommandService);
  private readonly store = inject(Store);

  public static readonly VORGANG_WITH_EINGANG_URL: string = 'vorgangWithEingangUrl';

  public getVorgangWithEingang(): Observable<StateResource<VorgangWithEingangResource>> {
    return combineLatest([this.facade.getVorgangWithEingang(), this.apiRootService.getApiRoot()]).pipe(
      tap(([vorgangWithEingang, apiRoot]) => {
        if (isNotNull(apiRoot.resource))
          doIfLoadingRequired(vorgangWithEingang, () => this.facade.loadVorgangWithEingang(this.getVorgangWithEingangUri()));
      }),
      map(([vorgangWithEingang]) => vorgangWithEingang),
      startWith(createEmptyStateResource<VorgangWithEingangResource>(true)),
    );
  }

  public getAttachments(): Observable<StateResource<BinaryFileListResource>> {
    return this.facade.getAttachmentList().pipe(
      withLatestFrom(this.facade.getVorgangWithEingang()),
      tap(([attachmentList, vorgangWithEingang]) =>
        doIfLoadingRequired(attachmentList, () => this.facade.loadAttachmentList(vorgangWithEingang.resource)),
      ),
      map(([attachmentList]) => attachmentList),
      startWith(createEmptyStateResource<BinaryFileListResource>(true)),
    );
  }

  public getRepresentations(): Observable<StateResource<BinaryFileListResource>> {
    return this.facade.getRepresentationList().pipe(
      withLatestFrom(this.facade.getVorgangWithEingang()),
      tap(([representationList, vorgangWithEingang]) =>
        doIfLoadingRequired(representationList, () => this.facade.loadRepresentationList(vorgangWithEingang.resource)),
      ),
      map(([representationList]) => representationList),
      startWith(createEmptyStateResource<BinaryFileListResource>(true)),
    );
  }

  public clearVorgang(): void {
    this.facade.clearVorgangWithEingang();
  }

  public setPendingSendPostfachMailCommand(command: StateResource<CommandResource>) {
    this.facade.setPendingSendPostfachMailSingleCommand(command);
  }
  public setPendingSendPostfachMailSingleCommandLoading(): void {
    this.facade.setPendingSendPostfachMailSingleCommandLoading();
  }
  public getPendingSendPostfachMailCommand(): Observable<StateResource<CommandResource>> {
    return this.facade.getSendPostfachNachrichtPendingCommand();
  }

  public setPendingForwardSingleCommandLoading(): void {
    this.facade.setForwardSingleCommandLoading();
  }
  public getPendingForwardCommand(): Observable<StateResource<CommandResource>> {
    return this.facade.getForwardPendingCommand();
  }
  public setPendingForwardSingleCommand(command: StateResource<CommandResource>): void {
    this.facade.setForwardSingleCommand(command);
  }

  public reloadCurrentVorgang(): void {
    this.facade.loadVorgangWithEingang(this.getVorgangWithEingangUri());
  }

  public reloadCurrentVorgangWithAddtionalActions(additionalActions: AdditionalActions): void {
    this.facade.loadVorgangWithEingangWithAdditionalActions(this.getVorgangWithEingangUri(), additionalActions);
  }

  getVorgangWithEingangUri(): ResourceUri {
    return this.navigationService.getDecodedParam(VorgangService.VORGANG_WITH_EINGANG_URL);
  }

  public assignUser(userUri: ResourceUri): Observable<StateResource<CommandResource>> {
    this.facade.assignUser(userUri);
    return this.facade.getAssignUserCommand().pipe(
      tapOnCommandSuccessfullyDone((commandStateResource) => {
        this.reloadVorgang(commandStateResource.resource);
      }),
    );
  }

  public unassignUser(): Observable<StateResource<CommandResource>> {
    this.facade.unassignUser();
    return this.facade.getUnassignUserCommand().pipe(
      tapOnCommandSuccessfullyDone((commandStateResource) => {
        this.reloadVorgang(commandStateResource.resource);
      }),
    );
  }

  public getBackButtonUrl(): Observable<string> {
    return this.facade.getBackButtonUrl();
  }

  public canNavigateToPathSegements(path: string): Observable<boolean> {
    const pathSegments: string[] = path.substring(1).split('/');
    const linkRel: string = VorgangNavigationUtil.buildLinkRelFromPathSegments(pathSegments);

    return this.apiRootService.getApiRoot().pipe(
      filter((apiRoot) => isNotNull(apiRoot.resource)),
      map((apiRoot) => hasLink(apiRoot.resource, linkRel)),
    );
  }

  public getVorgangExport(): Observable<StateResource<boolean>> {
    return this.facade.getVorgangExport();
  }

  public exportVorgang(vorgangWithEingang: VorgangWithEingangResource): void {
    this.facade.exportVorgang(vorgangWithEingang, VorgangWithEingangLinkRel.EXPORT_VORGANG);
  }

  public downloadExportVorgang(vorgangWithEingang: VorgangWithEingangResource): void {
    this.facade.exportVorgang(vorgangWithEingang, VorgangWithEingangLinkRel.DOWNLOAD_EXPORT_VORGANG);
  }

  public verakten(): Observable<StateResource<CommandResource>> {
    this.reloadCurrentVorgang();

    return this.getVorgangWithEingang().pipe(
      filter(isLoaded),
      first(),
      mapToResource(),
      switchMap((vorgangWithEingang: VorgangWithEingangResource) =>
        this.commandService.createCommand(this.createVorgangVeraktenCommandProps(vorgangWithEingang)),
      ),
    );
  }

  private createVorgangVeraktenCommandProps(vorgangWithEingang: VorgangWithEingangResource): CreateCommandActionProps {
    return {
      resource: vorgangWithEingang,
      linkRel: VorgangWithEingangLinkRel.VERAKTEN,
      command: { order: CommandOrder.VORGANG_VERAKTEN, body: {} },
      snackBarMessage: 'Vorgang in Veraktung.',
      snackBarErrorMessage: 'Ein Fehler ist aufgetreten.',
    };
  }

  public processVorgang(vorgangWithEingang: VorgangWithEingangResource): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand(this.createProcessVorgangCommandProps(vorgangWithEingang));
  }

  private createProcessVorgangCommandProps(vorgangWithEingang: VorgangWithEingangResource): CreateCommandActionProps {
    return {
      resource: vorgangWithEingang,
      linkRel: VorgangWithEingangLinkRel.PROCESS_VORGANG,
      command: createProcessVorgangCommand(this.envConfig.processorNames),
      snackBarMessage: 'Vorgang vorprüfen erfolgreich.',
    };
  }

  public setAktenzeichen(vorgang: VorgangWithEingangResource, aktenzeichen: string): Observable<StateResource<CommandResource>> {
    const createCommand = { order: CommandOrder.SET_AKTENZEICHEN, body: { aktenzeichen } };
    return this.commandService.createCommand({
      resource: vorgang,
      linkRel: VorgangWithEingangLinkRel.SET_AKTENZEICHEN,
      command: createCommand,
    });
  }

  public moveAndDelete(): Observable<StateResource<CommandResource>> {
    return this.createCommand({
      linkRel: VorgangWithEingangLinkRel.MOVE_AND_DELETE,
      command: createMoveAndDeleteCommand(),
      snackBarMessage: 'Der Vorgang wurde an die Lösch-Organisationseinheit weitergeleitet.',
    });
  }

  public createCommand(
    CreateCommandActionProps: CreateCommandActionPropsWithoutResource,
    reloadResource: boolean = false,
  ): Observable<StateResource<CommandResource>> {
    return this.selectVorgangWithEingang().pipe(
      first(),
      mapToResource(),
      switchMap((vorgang: VorgangWithEingangResource) =>
        this.commandService.createCommand({ ...CreateCommandActionProps, resource: vorgang }),
      ),
      tapOnCommandSuccessfullyDone((commandStateResource: StateResource<CommandResource>) => {
        if (reloadResource) this.reloadVorgang(commandStateResource.resource);
      }),
    );
  }

  public selectVorgangWithEingang(): Observable<StateResource<VorgangWithEingangResource>> {
    return this.facade.getVorgangWithEingang();
  }

  public reloadVorgang(commandResource: CommandResource): void {
    this.facade.loadVorgangWithEingang(getEffectedResourceUrl(commandResource));
  }

  public getAndPollPendingCommand(resourceUri: ResourceUri): Observable<StateResource<CommandResource>> {
    this.store.dispatch(VorgangActions.setPendingCommandLoading({ resourceUri }));
    return this.selectPendingCommand(resourceUri).pipe(
      mapToResource(),
      switchMap((command: CommandResource) => this._pollCommandIfPresent(resourceUri, command)),
    );
  }

  public selectPendingCommand(relatedResourceUri: ResourceUri): Observable<StateResource<CommandResource>> {
    return this.store.select(VorgangSelectors.getPendingCommand(relatedResourceUri));
  }

  _pollCommandIfPresent(resourceUri: ResourceUri, command: CommandResource): Observable<StateResource<CommandResource>> {
    if (isNotNil(command)) return this._pollCommand(resourceUri, command);
    return of(createEmptyStateResource<CommandResource>());
  }

  _pollCommand(resourceUri: ResourceUri, command: CommandResource): Observable<StateResource<CommandResource>> {
    return this.commandService
      .pollCommand(command)
      .pipe(
        tapOnCommandSuccessfullyDone((commandStateResource: StateResource<CommandResource>) =>
          this.updatePendingCommand(resourceUri, commandStateResource.resource),
        ),
      );
  }

  private updatePendingCommand(relatedResourceUri: ResourceUri, command: CommandResource): void {
    this.store.dispatch(VorgangActions.updatePendingCommand({ relatedResourceUri, command }));
  }
}
